import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useAuth0 } from "@auth0/auth0-react";
import * as Sentry from "@sentry/react";
import { isStaffEmail } from "@utils/user_utils";
import { CHATBOT_PATH, PUBLIC_PATHS } from "@routes";
import { SCREENGRAB_UI_ATTR } from "@components/Screengrab/captureRegion";
import { ChatProvider, useChatContext } from "../context";
import FloatingChatFab from "./FloatingChatFab";
import FloatingChatPanel from "./FloatingChatPanel";
import "./styles.css";
import "../tailwind.css";

// Same exclusion set as the retired ScreengrabFloat. The sandbox mounts its own
// Sandbox AI FAB, stacked above this widget's FAB, so /sandbox is not excluded. Matched as exact page OR its subpaths — a bare
// startsWith("/on") would also hide any future route that merely begins with "on".
const HIDDEN_PATHS = [CHATBOT_PATH, "/on", "/team-selection"];

// Whistle brand, not team colors — the assistant reads as a Whistle feature everywhere.
const BRAND_VARS = {
  "--team-primary": "#F15923",
  "--team-font-color": "#ffffff",
} as React.CSSProperties;

const UI = { [SCREENGRAB_UI_ATTR]: "" }; // widget never appears in screenshot captures

// A team switch swaps the gpsdv-team-id axios header in place without remounting
// anything above <Routes>, so the always-mounted provider would otherwise carry the
// previous team's conversation (and its conversationId) into the new team's context.
const TeamConversationReset: React.FC = () => {
  const { resetConversation } = useChatContext();
  const selectedTeam = useSelector(({ manager }: any) => manager?.selectedTeam);
  const prevTeamRef = useRef(selectedTeam);
  useEffect(() => {
    if (prevTeamRef.current !== selectedTeam) {
      prevTeamRef.current = selectedTeam;
      resetConversation();
    }
  }, [selectedTeam, resetConversation]);
  return null;
};

const FloatingChatRootInner: React.FC = () => {
  const { pathname } = useLocation();
  const { user } = useAuth0();
  const dvai_enabled = useSelector(({ team }: any) => team?.info?.dvai_enabled);
  const [open, setOpen] = useState(false);
  const [capturing, setCapturing] = useState(false);

  // The gate/path checks hide the UI but never unmount the provider — the widget's
  // conversation must survive a pass through /chatbot or any other hidden route.
  const gated = !(dvai_enabled || isStaffEmail(user?.email));
  // React Router does not strip trailing slashes — "/features/" must still exact-match.
  const path = pathname.replace(/\/+$/, "") || "/";
  const hidden =
    gated ||
    PUBLIC_PATHS.includes(path) ||
    HIDDEN_PATHS.some(p => path === p || path.startsWith(p + "/"));
  // The sandbox corner stacks this FAB with the Sandbox AI FAB: the modifier class
  // drops the attention pulse there.
  const onSandbox = path === "/sandbox" || path.startsWith("/sandbox/");

  return (
    <ChatProvider>
      <TeamConversationReset />
      {!hidden && (
        <div
          className={`floating-chat-root chat-v2-root${capturing ? " floating-chat-root--capturing" : ""}${onSandbox ? " floating-chat-root--sandbox" : ""}`}
          style={BRAND_VARS}
          {...UI}
        >
          <FloatingChatFab open={open} onToggle={() => setOpen(o => !o)} />
          <FloatingChatPanel
            open={open}
            onClose={() => setOpen(false)}
            onCaptureOpenChange={setCapturing}
            capturing={capturing}
          />
        </div>
      )}
    </ChatProvider>
  );
};

// The widget mounts as a sibling of <Routes> in an app with no other error boundary:
// without this, any render error inside it would blank the entire app. On error the
// widget simply disappears (and Sentry records the exception).
const FloatingChatRoot: React.FC = () => (
  <Sentry.ErrorBoundary fallback={<></>}>
    <FloatingChatRootInner />
  </Sentry.ErrorBoundary>
);

export default FloatingChatRoot;
